import * as React from "react";
import { isAxiosError } from "axios";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/taskpane/components/ui/dialog";
import { Button } from "@/taskpane/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/taskpane/components/ui/input-otp";
import useVerify2fa from "@/hooks/useVerify2fa";
import useVerifyBackupCode from "@/hooks/useVerifyBackupCode";
import { BACKUP_CODE_LENGTH, OTP_FAILURE_LIMIT, OTP_LENGTH } from "@/config/auth.config";
import {
  backupCodeFormSchema,
  otpFormSchema,
  type BackupCodeFormValues,
  type OtpFormValues,
} from "@/schemas/auth.schemas";

const CodeInput: React.FC<{
  length: number;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  ariaLabel: string;
  ariaInvalid: boolean;
}> = ({ length, value, onChange, disabled, ariaLabel, ariaInvalid }) => (
  <div className="flex justify-center">
    <InputOTP
      maxLength={length}
      value={value}
      onChange={onChange}
      disabled={disabled}
      autoFocus
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
    >
      <InputOTPGroup>
        {Array.from({ length }).map((_, index) => (
          <InputOTPSlot key={index} index={index} />
        ))}
      </InputOTPGroup>
    </InputOTP>
  </div>
);

const VerifyButton: React.FC<{
  isPending: boolean;
  disabled: boolean;
  className?: string;
}> = ({ isPending, disabled, className }) => (
  <Button type="submit" size="lg" className={className} disabled={disabled}>
    {isPending ? (
      <>
        <Loader className="animate-spin" />
        Verifying...
      </>
    ) : (
      "Verify"
    )}
  </Button>
);

interface TwoFactorDialogProps {
  open: boolean;
  pendingAccessToken: string;
  onOpenChange: (open: boolean) => void;
  onVerified: () => void;
  onExpired: () => void;
}

const OtpForm: React.FC<{
  pendingAccessToken: string;
  onVerified: () => void;
  onExpired: () => void;
  onTooManyFailures: () => void;
}> = ({ pendingAccessToken, onVerified, onExpired, onTooManyFailures }) => {
  const verifyMutation = useVerify2fa({ pendingAccessToken, onVerified, onExpired });
  // TanStack Query guarantees `mutate` has a stable identity for the lifetime
  // of the component. The ref guard below prevents the race between auto-submit
  // on the 6th digit and a manual Enter press.
  const isSubmittingRef = React.useRef(false);
  const failuresRef = React.useRef(0);
  const { mutate } = verifyMutation;

  const { control, handleSubmit, formState, setValue, reset } = useForm<OtpFormValues>({
    resolver: zodResolver(otpFormSchema),
    mode: "onChange",
    defaultValues: { otp: "" },
  });

  const submit = handleSubmit(({ otp }) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    mutate(
      { otp },
      {
        onError: (error) => {
          // Only count wrong-code failures. Expired sessions (401) close the
          // dialog via onExpired; rate-limit (429) is transient.
          const status = isAxiosError(error) ? error.response?.status : undefined;
          if (status !== 401 && status !== 429) {
            failuresRef.current += 1;
            // Always clear the input — even on the final failure, since the
            // OtpForm may remount later via "Use authenticator instead".
            reset({ otp: "" });
            if (failuresRef.current >= OTP_FAILURE_LIMIT) {
              onTooManyFailures();
            }
          }
        },
        onSettled: () => {
          isSubmittingRef.current = false;
        },
      }
    );
  });

  return (
    <form onSubmit={submit} className="flex flex-col gap-4 mt-4" noValidate>
      <p className="text-sm text-neutral-400 m-0">
        Enter the 6-digit code from your authenticator app.
      </p>
      <Controller
        control={control}
        name="otp"
        render={({ field, fieldState }) => (
          <CodeInput
            length={OTP_LENGTH}
            value={field.value}
            onChange={(value) => {
              setValue("otp", value, { shouldValidate: true });
              if (value.length === OTP_LENGTH) {
                // Auto-submit once the final digit is typed. RHF validates
                // via the resolver before the mutation fires.
                void submit();
              }
            }}
            disabled={verifyMutation.isPending}
            ariaLabel="Authenticator code"
            ariaInvalid={fieldState.invalid}
          />
        )}
      />
      <VerifyButton
        isPending={verifyMutation.isPending}
        disabled={!formState.isValid || verifyMutation.isPending}
        className="self-center"
      />
    </form>
  );
};

const BackupCodeHeader: React.FC = () => (
  <div className="text-center">
    <p className="block text-sm text-danger-200 m-0">Too many failed attempts.</p>
    <p className="text-sm text-neutral-400 m-0">
      Enter one of your 6-digit backup codes to continue.
    </p>
  </div>
);

const UseAuthenticatorLink: React.FC<{ onClick: () => void; disabled: boolean }> = ({
  onClick,
  disabled,
}) => (
  <Button
    type="button"
    variant="link"
    className="text-primary-300 p-0 h-auto self-center"
    onClick={onClick}
    disabled={disabled}
  >
    Use authenticator instead
  </Button>
);

const BackupCodeForm: React.FC<{
  pendingAccessToken: string;
  onVerified: () => void;
  onExpired: () => void;
  onUseAuthenticator: () => void;
}> = ({ pendingAccessToken, onVerified, onExpired, onUseAuthenticator }) => {
  const verifyMutation = useVerifyBackupCode({ pendingAccessToken, onVerified, onExpired });
  const isSubmittingRef = React.useRef(false);
  const { mutate } = verifyMutation;

  const { control, handleSubmit, formState, setValue, reset } = useForm<BackupCodeFormValues>({
    resolver: zodResolver(backupCodeFormSchema),
    mode: "onChange",
    defaultValues: { code: "" },
  });

  const submit = handleSubmit(({ code }) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    mutate(
      { code },
      {
        onError: (error) => {
          // Clear the input on a wrong-code error so the user can retype.
          // 401 (expired) closes the dialog; 429 (rate limit) should keep
          // the code so the user can retry when the limiter clears.
          const status = isAxiosError(error) ? error.response?.status : undefined;
          if (status !== 401 && status !== 429) reset({ code: "" });
        },
        onSettled: () => {
          isSubmittingRef.current = false;
        },
      }
    );
  });

  return (
    <form onSubmit={submit} className="flex flex-col gap-4 mt-4" noValidate>
      <BackupCodeHeader />
      <Controller
        control={control}
        name="code"
        render={({ field, fieldState }) => (
          <CodeInput
            length={BACKUP_CODE_LENGTH}
            value={field.value}
            onChange={(value) => {
              setValue("code", value, { shouldValidate: true });
              if (value.length === BACKUP_CODE_LENGTH) void submit();
            }}
            disabled={verifyMutation.isPending}
            ariaLabel="Backup code"
            ariaInvalid={fieldState.invalid}
          />
        )}
      />
      <UseAuthenticatorLink onClick={onUseAuthenticator} disabled={verifyMutation.isPending} />
      <VerifyButton
        isPending={verifyMutation.isPending}
        disabled={!formState.isValid || verifyMutation.isPending}
        className="self-center"
      />
    </form>
  );
};

const TwoFactorDialog: React.FC<TwoFactorDialogProps> = ({
  open,
  pendingAccessToken,
  onOpenChange,
  onVerified,
  onExpired,
}) => {
  const [useBackup, setUseBackup] = React.useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader className="sm:text-center">
          <DialogTitle className="flex items-center justify-center gap-2">
            <ShieldCheck className="text-primary-300" size={20} />
            Two-Factor Authentication
          </DialogTitle>
          <DialogDescription>Verify your identity to finish signing in.</DialogDescription>
        </DialogHeader>
        {useBackup ? (
          <BackupCodeForm
            pendingAccessToken={pendingAccessToken}
            onVerified={onVerified}
            onExpired={onExpired}
            // Remounting OtpForm resets its local failure counter. The
            // backend's 5/min rate limit on /auth/2mfa/verify is the
            // authoritative lockout; the client counter is UX-only.
            onUseAuthenticator={() => setUseBackup(false)}
          />
        ) : (
          <OtpForm
            pendingAccessToken={pendingAccessToken}
            onVerified={onVerified}
            onExpired={onExpired}
            onTooManyFailures={() => setUseBackup(true)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorDialog;
